import posthog, { type PostHogConfig } from 'posthog-js';
import { dropExceptionNoise } from './exception-noise';

/**
 * Low-level PostHog client lifecycle for the analytics module.
 *
 * Everything here is defensive: if the public key is missing, or we are running
 * on the server, or PostHog throws internally, these functions must never throw
 * and never block the caller. Analytics is strictly fire-and-forget.
 */

let initialized = false;

/**
 * Whether `$exception` error tracking is active for this session. Set once in
 * `initAnalytics()` from `NEXT_PUBLIC_POSTHOG_ERROR_TRACKING` (ON unless the env
 * var is exactly `'false'`) and read by `captureException`.
 */
let errorTrackingEnabled = false;

type Env = Record<string, string | undefined>;

/** `'true'` / `'1'` / `'yes'` (any case, trimmed) → true; anything else → false. */
function flag(env: Env, name: string): boolean {
  const value = (env[name] ?? '').trim().toLowerCase();
  return value === 'true' || value === '1' || value === 'yes';
}

/**
 * Build the SDK init config from environment.
 *
 * Free-tier posture by default (PostHog's free limits are 1M events and 5K
 * session replays / month):
 *
 * - `autocapture` is OFF unless `NEXT_PUBLIC_POSTHOG_AUTOCAPTURE` is truthy.
 *   Autocapture turns every click into an event and is the main way a busy app
 *   blows past the event limit; the module's ~23 explicit `capture()` calls are
 *   low-volume and always on.
 * - Session replay is OFF unless `NEXT_PUBLIC_POSTHOG_SESSION_REPLAY` is truthy
 *   (`disable_session_recording: true`). All replay masking stays configured and
 *   every `data-ph-block` attribute stays in the tree, so turning it on is a
 *   single env var, no code change.
 * - `NEXT_PUBLIC_POSTHOG_REPLAY_SAMPLE_RATE` (0–1) optionally records only a
 *   fraction of sessions when replay is on.
 *
 * `session_recording.blockSelector` carries only the generic `[data-ph-block]`
 * opt-out hook: the attribute is placed directly on the root element of every
 * blocked surface (live video, screen share, chat), so no per-selector list is
 * needed here.
 *
 * `mask_all_text` / `mask_all_element_attributes` extend the replay masking to
 * autocapture: without them an enabled `autocapture` would ship `$el_text` and
 * element attributes (participant names, chat labels) as plain event properties,
 * bypassing the replay text masking.
 *
 * Error tracking (`capture_exceptions`) is ON by default — the opposite posture
 * to autocapture / replay — because crashes are rare, high-value, and PostHog
 * rate-limits autocaptured exceptions (free tier is 100K/month). It captures
 * unhandled errors and unhandled promise rejections as `$exception` events and
 * lazy-loads an autocapture chunk from `/ingest/static/...` at runtime. Set
 * `NEXT_PUBLIC_POSTHOG_ERROR_TRACKING=false` (that literal string) to disable.
 * `before_send: dropExceptionNoise` drops known non-actionable exceptions
 * (video-element races, chunk-load failures) — see `./exception-noise`.
 *
 * Exported for unit testing; `initAnalytics` calls it with `process.env`.
 */
export function resolvePosthogConfig(env: Env = process.env): Partial<PostHogConfig> {
  const sessionReplayEnabled = flag(env, 'NEXT_PUBLIC_POSTHOG_SESSION_REPLAY');

  const rawSampleRate = (env.NEXT_PUBLIC_POSTHOG_REPLAY_SAMPLE_RATE ?? '').trim();
  const parsedSampleRate = Number(rawSampleRate);
  const sampleRate =
    rawSampleRate !== '' && Number.isFinite(parsedSampleRate)
      ? Math.min(1, Math.max(0, parsedSampleRate))
      : undefined;

  return {
    api_host: '/ingest',
    ui_host: env.NEXT_PUBLIC_POSTHOG_UI_HOST || 'https://eu.posthog.com',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: flag(env, 'NEXT_PUBLIC_POSTHOG_AUTOCAPTURE'),
    capture_exceptions: env.NEXT_PUBLIC_POSTHOG_ERROR_TRACKING !== 'false',
    before_send: dropExceptionNoise,
    mask_all_text: true,
    mask_all_element_attributes: true,
    person_profiles: 'identified_only',
    disable_session_recording: !sessionReplayEnabled,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      blockSelector: '[data-ph-block]',
      collectFonts: true,
      ...(sampleRate !== undefined ? { sampleRate } : {}),
    },
  };
}

/**
 * Initialise the PostHog browser SDK exactly once.
 *
 * Idempotent and client-only: repeated calls, or any call during SSR, are
 * no-ops. When `NEXT_PUBLIC_POSTHOG_KEY` is absent the SDK is left uninitialised
 * and every downstream analytics call degrades to a no-op.
 */
export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined') {
    return;
  }

  errorTrackingEnabled = process.env.NEXT_PUBLIC_POSTHOG_ERROR_TRACKING !== 'false';

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) {
    console.warn('[analytics] NEXT_PUBLIC_POSTHOG_KEY not set — analytics disabled');
    return;
  }

  try {
    posthog.init(key, resolvePosthogConfig());
    initialized = true;
  } catch (error) {
    console.warn('[analytics] posthog.init failed', error);
  }
}

/**
 * The PostHog singleton, or `null` when analytics was never initialised.
 * Use this for call sites that live outside React (event handlers, stores).
 */
export function getPostHog(): typeof posthog | null {
  return initialized ? posthog : null;
}

/**
 * Non-hook event capture for the rare call site that cannot use
 * `useAnalytics()`. Silently no-ops when analytics is uninitialised and
 * swallows any error the SDK raises.
 */
export function captureEvent(
  event: string,
  props?: Record<string, unknown>,
  options?: { send_instantly?: boolean },
): void {
  try {
    getPostHog()?.capture(event, props, options);
  } catch (error) {
    console.warn('[analytics] captureEvent failed', error);
  }
}

/**
 * Report a caught error to PostHog as a `$exception` event. A guarded no-op
 * when error tracking is disabled (`NEXT_PUBLIC_POSTHOG_ERROR_TRACKING=false`)
 * or analytics was never initialised; swallows any error the SDK raises.
 */
export function captureException(
  error: unknown,
  props?: Record<string, unknown>,
): void {
  if (!errorTrackingEnabled) return;
  try {
    getPostHog()?.captureException(error, props);
  } catch (e) {
    console.warn('[analytics] captureException failed', e);
  }
}
